import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import { DropEvent, FileRejection, useDropzone } from "react-dropzone";
import { AutoDetectSourceLanguagesOpenRangeOptionName } from "microsoft-cognitiveservices-speech-sdk/distrib/lib/src/common.speech/Exports";
import { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import ReadExcel from "./ImportExcelReader";
import { Button } from "./ui/button";
import cloud from "../../TDD-Images/cloud.svg";
import { pdfjs } from 'react-pdf';

// Set the worker source for PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Component for uploading and processing PDF files
const UploadFile = ({
  defaultFileName,
  handleFunc,
  handleDefaultRemove,
  contentChange,
}: {
  defaultFileName?: string
  handleFunc?: (resume: string, resumeName: string) => void;
  contentChange?: (file: File | null) => void;
  handleDefaultRemove?: () => void;
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [removedDefaultFile, setremovedDefaultFile] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [showError, setshowError] = useState<string | null>(null);

  // This function handles the file upload process, reads the PDF file, and extracts text content from it.
  // It returns a promise that resolves with the extracted text data or an error message if the extraction fails.
  const handleFileUpload = (selectedFile: File): Promise<{ data: string, error?: string }>=> {
    return new Promise((resolve, reject) => {
    if (selectedFile) {
      const fileReader = new FileReader();
      let resume = "";
      fileReader.onload = async () => {
        const typedArray = new Uint8Array(fileReader.result as ArrayBuffer);
        const pdfData = typedArray.buffer;
        try {
          const loadingTask = pdfjs.getDocument({ data: pdfData });
          const pdf = await loadingTask.promise;

          for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map((item: any) => item.str !== '' ? item.str : '\n').join('');
            resume = `${resume}${pageText}\n`;
          }
          resolve({
            data: resume
          });

        } catch (error) {
          resolve({
            data: "",
            error: error instanceof Error ? error.message : 'Unknown error occurred'
          });
        }
      };
      fileReader.readAsArrayBuffer(selectedFile);
    }
  });
  }

  // This function is called when files are dropped into the dropzone. It handles the file upload process by
  // setting the uploaded file state, calling the handleFileUpload function, and managing the loading and error states.
  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setUploadedFile(acceptedFiles[0]);
      handleFileUpload(acceptedFiles[0]).then((result) => {
        setLoading(false);
        if (result.error) {
          setshowError(`could not upload file: ${result.error}.`);
        } else {
          if (handleFunc) {
            handleFunc(result.data, acceptedFiles[0].name);
          }
          setshowError(null);
        }
      });
      setLoading(true);
      setshowError(null);
    } else {
      setUploadedFile(null);
    }
  };

  // This effect runs when the uploadedFile state changes. It handles the default file removal and content change
  // by updating the removedDefaultFile state and calling the contentChange function if provided.
  useEffect(() => {
    if(!removedDefaultFile && defaultFileName && defaultFileName != "" && uploadedFile !== null){
    setremovedDefaultFile(true);
    }
    if (contentChange && uploadedFile == null) {
      contentChange(uploadedFile);
    }
  }, [uploadedFile]);

  // This function handles the removal of the uploaded file by resetting the uploadedFile and showError states.
  const handleFileRemove = () => {
    setUploadedFile(null);
    setshowError(null);
  };

  // Configure the dropzone for file uploads
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [
        ".pdf",
      ],
    },
    onError: (err: Error) => setshowError("could not upload file"),
    onDropRejected: (fileRejections: FileRejection[], event: DropEvent) =>
      setshowError("could not upload file"),
    maxFiles: 1,
  }); // Only accept one file}); // Accept Excel files });

  return (
    <Box
      {...getRootProps()}
      sx={{
        cursor: "pointer",
        display: "flex", // Flexbox for centering
        flexDirection: "column",
        border: "1px dashed #383838", // Dashed border
        justifyContent: "center", // Horizontally center content
        alignItems: "center", // Vertically center content
        width: "95%", // Set width
        padding: "1.5rem",
        borderRadius: "8px", // Optional rounded corners
        margin: "16px", // Spacing around the rectangle
      }}
    >
      <input {...getInputProps()} />

      <div className="flex justify-center items-center flex-col gap-2">
        <img src={cloud} alt="Upload Cloud" className="w-[36px]" />
        <p className="m-0 p-0 text-[#0B0B0B] text-[14px]">
          Drag your file(s) or browse
        </p>
        <p className="m-0 p-0 text-[#A3A7AF] text-[14px]">
          10 MB files are the maximum allowed
        </p>

        <Button 
          type="button"
          variant={"outline"}
          className="text-black"
        >
          Upload
        </Button>
      </div>
        {defaultFileName && defaultFileName != "" && !removedDefaultFile && (
          <Chip
            label={defaultFileName}
            onDelete={()=>{setremovedDefaultFile(true); handleDefaultRemove && handleDefaultRemove();}}
            deleteIcon={<CancelIcon />}
            color="primary"
            style={{ marginTop: "0.5rem" }}
          />
        )}

      {loading ? (
        <Box sx={{ mt: 2 }}>
          <CircularProgress />
          <Typography variant="body2" color="textSecondary">
            Uploading...
          </Typography>
        </Box>
      ) : (
        <>

          {uploadedFile && (
            <Chip
              label={uploadedFile.name}
              onDelete={handleFileRemove}
              deleteIcon={<CancelIcon />}
              color="primary"
              style={{ marginTop: "0.5rem" }}
            />
          )}

          {showError && (
            <Box sx={{ mt: 2, color: "red", textAlign: "left" }}>
              <Typography variant="subtitle1" gutterBottom>
                {showError}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default UploadFile;
